import React, { useEffect, useState, useMemo } from 'react';
import { Autocomplete, Button, Chip, DialogActions, DialogContent, Stack, TextField } from "../../styles/MaterialUI";
import axios from 'axios';
import apiConstants from '../../constants/apiConstants';
import { buildContentTypeOptions } from '../../utils/contentTypeUtils';
import { useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import ToolTip from '../../utils/CursorTooltip'
import { useOrgConfigData } from "../../hooks/useOrgConfigData";

const MapScreenDialog = ({ showAlert, mappingConfirmation, closeMapScreenModal }) => {
    const { selected_data } = useSelector((state) => state.mapScreenData);
    const { userToken } = useSelector((state) => state.userToken);
    const { organizationId } = useSelector((state) => state.userLogin);
    const orgConfig = useOrgConfigData("mapScreen");
    const {
        org_properties: org_configurations,
        all_content_types,
        permitted_content_types
    } = orgConfig || {};
    const headers = { Authorization: `Bearer ${userToken}` };
    const mapScreenInputs = { id: [], screenname: [] };
    const [mapScreenModalInputs, setMapScreenModalInputs] = useState(mapScreenInputs);
    const [contentType, setContentType] = useState(null);
    const [mapScreenNotes, setMapScreenNotes] = useState("");
    const [contentList, setContentList] = useState([{ id: "", name: "" }]);
    const [selectedContent, setSelectedContent] = useState({ id: "", name: "" });
    const contentTypeNames = { "Image": "image", "Video": "video", "IntelliDash Dashboard": "dashboard", "Content Playlist": "content_playlist", "Machine Metrics": "orchestrated_url", "Acumatica": "orchestrated_url", "Power BI": "orchestrated_url", "Open URL": "open_url" }
    const [integrationActiveFlag, setIntegrationActiveFlag] = useState(0);
    const [orgIntegrationData, setOrgIntegrationData] = useState([]);
    // At the top of MapScreenDialog, after useSelector:
    const allContentTypes = all_content_types ?? [];
    const permittedContentTypes = permitted_content_types ?? [];

    const contentTypeOptions = useMemo(() => {
        const options = buildContentTypeOptions(
            allContentTypes,
            permittedContentTypes,
            orgIntegrationData
        );

        // const getId = (id) =>
        //     id ? Number(id.replace(/\D/g, "")) : 9999;

        const sorted = [...options].sort((a, b) => {
            // // 1. enabled first
            // if (a.disabled !== b.disabled) {
            //     return a.disabled ? 1 : -1;
            // }

            // 2. optional: sort by ID inside each group
            return a.ic_content_type_name - b.ic_content_type_name;
        });

        return sorted;
    }, [allContentTypes, permittedContentTypes, orgIntegrationData]);


    // Derived selected option object for controlled value
    const selectedTypeOption = contentTypeOptions.find((o) => o.label === contentType) ?? null;
    const handleChangeType = async (value, contentId = "", contentName = "") => {
        setContentList([{ id: "", name: "" }]);
        setSelectedContent({ id: contentId, name: contentName });
        setContentType(value);

        const inputValue = contentTypeNames[value];
        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}getContents`, { org_id: "test" }, { headers });

            const { data } = response.data;
            const filteredData = data.filter((item) => item.ic_content_type === inputValue && item.ic_content_status === 1);
            const selectedIntegration = await getIntegrationByName(value);

            let contentListNew = filteredData
                .filter(item => {
                    if (value === "IntelliDash Dashboard") {
                        return !item.ic_org_integration_id;
                    }

                    if (selectedIntegration) {
                        return item.ic_org_integration_id === selectedIntegration.ic_org_integration_id;
                    }

                    return true; // fallback (e.g. "All")
                })
                .map(item => ({
                    id: item.ic_content_id,
                    name: item.ic_content_name
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            setContentList(contentListNew);
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    };

    const getIntegrationsData = async () => {
        const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
        const { data } = apiResponse.data;

        setOrgIntegrationData(data);

    }

    useEffect(() => {
        const handleScreenMapping = () => {
            const data_length = selected_data.length;
            const [data] = selected_data;

            if (data_length === 1 && data.ic_content_type !== null) {
                const contentType = data.ic_content_type;
                handleChangeType(contentType, data.ic_content_id, data.ic_content_name);
            }
            setMapScreenModalInputs((prev) => ({
                ...prev,
                id: selected_data.map((data) => data.screen_uiid), screenname: selected_data.map((data) => data.ic_screen_name),
            }));
        };

        handleScreenMapping();
        getIntegrationsData();
    }, []);

    const handleChange = (event, value) => {
        if (value) {
            setSelectedContent(value);
        } else {
            setSelectedContent({ id: "", name: "" });
        }
    };

    const updateMappingNotes = (event) => {
        const { value } = event.target;
        if (value.length <= 100) {
            setMapScreenNotes(value);
        }
    }

    const handlePublish = () => {
        const objectData = {
            contentType: contentType, contentName: selectedContent.name, screenuiid: mapScreenModalInputs.id,
            contentID: selectedContent.id, contentNotes: mapScreenNotes, mapScreenModalInputs
        };
        mappingConfirmation(objectData);
    }

    const getIntegrationByName = async (name) => {
        if (orgIntegrationData && orgIntegrationData.length > 0) {
            return orgIntegrationData.find(
                (item) => item.integration_name === name
            );
        }
        else {
            const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
            const { data } = apiResponse.data;
            setOrgIntegrationData(data);
            return data.find(
                (item) => item.integration_name === name
            );
        }

    };

    const isIntegrationActive = (name) => {
        return orgIntegrationData.some(
            (item) =>
                item.integration_name === name &&
                item.ic_activeflag === 1
        );
    };
    return (
        <>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <TextField size="small" value="" label="Screen Name" disabled
                        InputProps={{
                            startAdornment: mapScreenModalInputs.screenname.map((chip) => {
                                return (
                                    <Chip key={chip} label={chip} style={{ margin: "2px" }} />
                                );
                            }),
                        }}
                    />
                    {/* <Autocomplete size="small" value={contentType} onChange={(e, value) => handleChangeType(value)}
                        // options={["IntelliDash Dashboard", "Image", "Video", "Content Playlist", "Machine Metrics"]}
                        // getOptionDisabled={(option) => option === "Machine Metrics" && integrationActiveFlag !== 1}
                        options={
                            ["IntelliDash Dashboard", "Image", "Video", "Content Playlist", "Machine Metrics", "Acumatica", "Open URL"].filter((option) => {
                                if (option === "Machine Metrics") {
                                    return isIntegrationActive("Machine Metrics");
                                }
                                if (option === "Acumatica") {
                                    return isIntegrationActive("Acumatica");
                                }
                                return true;
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Content Type" />
                        )}
                    /> */}
                    <Autocomplete
                        size="small"
                        value={selectedTypeOption}
                        options={contentTypeOptions}
                        getOptionLabel={(option) => option?.label ?? ""}
                        getOptionDisabled={(option) => option.disabled}
                        isOptionEqualToValue={(option, value) =>
                            typeof value === "string"
                                ? option.label === value
                                : option.label === value?.label
                        }
                        onChange={(e, newValue) => handleChangeType(newValue?.label ?? null)}
                        renderOption={(props, option, { index }) => {
                            const isIntegrationType = ["Machine Metrics", "Acumatica", "Power BI"].includes(option.label);
                            const integrationInactive = isIntegrationType && !isIntegrationActive(option.label);

                            const tooltipText = option.disabled
                                ? integrationInactive
                                    ? "Integration not configured or inactive"
                                    : "Upgrade to create this content type"
                                : "";

                            // Show a divider right before the first disabled option in the list
                            const isFirstDisabled =
                                option.disabled &&
                                (index === 0 || !contentTypeOptions[index - 1]?.disabled);

                            return (
                                <React.Fragment key={option.label}>
                                    {isFirstDisabled && (
                                        <div style={{ borderTop: "0.5px solid #afa1a1", margin: "2px 0" }} />
                                    )}
                                    <ToolTip
                                        title={tooltipText}
                                        disableHoverListener={!option.disabled}
                                        disableTouchListener={!option.disabled}
                                    >
                                        <span style={{ display: "block", width: "100%" }}>
                                            <li
                                                {...props}
                                                aria-disabled={option.disabled}
                                                style={{
                                                    ...props.style,
                                                    color: option.disabled ? "#574d4d" : "inherit",
                                                    cursor: option.disabled ? "not-allowed" : "pointer",
                                                    pointerEvents: option.disabled ? "none" : "auto",
                                                    opacity: option.disabled ? 0.6 : 1,
                                                }}
                                            >
                                                {option.label}
                                            </li>
                                        </span>
                                    </ToolTip>
                                </React.Fragment>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Content Type" />
                        )}
                    />
                    <Autocomplete size="small" value={selectedContent || { id: "", name: "" }} options={contentList} onChange={handleChange} getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Available Content for Mapping" />
                        )} disabled={contentType === "Machine Metrics" && isIntegrationActive("Machine Metrics") !== true || contentType === "Acumatica" && isIntegrationActive("Acumatica") !== true || contentType === "Power BI" && isIntegrationActive("Power BI") !== true}
                    />
                    <Stack spacing={1} direction="row">
                        <TextField value={mapScreenNotes} id="outlined-multiline" label="Notes" multiline rows={2} variant="outlined"
                            onChange={updateMappingNotes} style={{ width: "100%" }} inputProps={{ maxLength: 100 }}
                            helperText={`${mapScreenNotes ? mapScreenNotes.length : 0}/100 characters.`} />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
                {/* Required for phase-3 release */}
                {/* <Button variant="contained" size="small" color="info"
                    sx={{
                        textTransform: "none", marginLeft: "20px",
                        "&:hover": {
                            bgcolor: "#026faa",
                        },
                    }}
                >
                    Preview
                </Button> */}
                <Stack direction="row" spacing={1} sx={{ marginRight: "20px" }}>
                    <Button variant="contained" size="small" onClick={handlePublish}
                        sx={{ textTransform: "none", bgcolor: "#003366", "&:hover": { bgcolor: "#0d45a5", }, }}
                    >
                        Publish
                    </Button>
                    <Button variant="contained" size="small" color="error" onClick={closeMapScreenModal} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                </Stack>
            </DialogActions>
        </>
    )
}

export default MapScreenDialog;
